'use client'

import { useMemo } from 'react'
import {
  IconArrowLeft,
  IconFileDownload,
  IconPrinter
} from '@tabler/icons-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'

import { sortCartItemsByCategory } from 'modules/cart'
import { QueryKey } from 'shared/api'
import { IS_TMS_DELIVERY } from 'shared/config'
import Button from 'shared/ui/Button'
import { ButtonSizes } from 'shared/ui/Button/Button.enums'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import { Tooltip } from 'shared/ui/Tooltip'
import toast from 'shared/ui/utils/toast'
import { noop } from 'shared/utils/common'
import { getApiErrors } from 'shared/utils/errors'
import { PATHS } from 'shared/utils/paths'

import { getSaleOrderDetails } from '../../api'
import CancelSaleOrderModal from '../../components/CancelSaleOrderModal'
import OrderReturnStatusBadge from '../../components/ProfileSaleOrderReturnStatusBadge'
import RepeatSaleOrderButton from '../../components/RepeatSaleOrderButton'
import SaleOrderStatus from '../../components/SaleOrderStatus'
import EditSaleOrderLabel from '../../submodules/editSaleOrder/components/EditSaleOrderLabel'
import { isEditSaleOrderEnabled } from '../../submodules/editSaleOrder/utils'
import { downloadSaleOrderDocument } from '../../submodules/invoices/utils'
import { getPaymentLinkForSaleOrder } from '../../submodules/payment/api'
import { redirectToPaymentLink } from '../../submodules/payment/utils'
import { Invoice, Receipt, SaleOrderReturnStatus } from '../../types'

import ProfileSaleOrderDetailsProductList from './components/ProfileSaleOrderDetailsProductList'
import ProfileSaleOrderDetailsTable from './components/ProfileSaleOrderDetailsTable'
import { generateOrderPdf } from './components/ProfileSaleOrderPdfDocument'
import OrderReturnModal from './components/ProfileSaleOrderReturnModal'

import styles from './ProfileSaleOrderDetails.module.scss'

interface ProfileSaleOrderDetailsProps {
  orderId: string
  token?: string
}

const ProfileSaleOrderDetails = ({ orderId, token }: ProfileSaleOrderDetailsProps) => {
  const router = useRouter()
  const queryClient = useQueryClient()

  const { data } = useQuery({
    queryKey: [QueryKey.profileOrders, orderId, token],
    queryFn: () => getSaleOrderDetails({ token, id: orderId })
  })

  const currentOrder = data?.data?.data

  const handleGoBack = async () => {
    router.push(PATHS.profileOrders)
  }

  const isEditable = useMemo((): boolean => {
    if (!currentOrder) return false

    return isEditSaleOrderEnabled(currentOrder)
  }, [currentOrder])

  const handleReturnSubmitted = async () => {
    await queryClient.invalidateQueries({ queryKey: [QueryKey.profileOrders] })
    await queryClient.refetchQueries({
      queryKey: [QueryKey.profileOrders, orderId, token],
      exact: true
    })
  }

  const [items, receipts, invoices, code, status, returnStatus] = useMemo(
    () => [
      currentOrder?.items ?? [],
      currentOrder?.receipts ?? [],
      currentOrder?.invoices ?? [],
      currentOrder?.code,
      currentOrder?.status,
      currentOrder?.returnStatus
    ],
    [currentOrder]
  )

  const isCancelEnabledByStatus = useMemo(
    () => !['delivered', 'cancelled', 'in_progress'].includes(status ?? ''),
    [status]
  )

  const documentsList = useMemo(
    () => [...invoices, ...receipts],
    [invoices, receipts]
  )

  const productsByCategory = useMemo(
    () => sortCartItemsByCategory(items),
    [items]
  )

  if (!currentOrder) {
    return (
      <div className={styles.emptyStateWrapper}>
        <LoadingIndicator size={40} />
      </div>
    )
  }

  const handlePaymentStart = () => {
    getPaymentLinkForSaleOrder({
      orderId: currentOrder?.id || '',
      token
    })
      .then(async response => {
        const paymentLink = response.data.data?.link || null

        if (paymentLink) {
          redirectToPaymentLink(paymentLink)
        }
      })
      .catch((err: unknown) => {
        toast.error(getApiErrors(err))
      })
  }

  const handleGenerateOrderPdf = async () => {
    await generateOrderPdf(currentOrder, productsByCategory)
  }

  const handleDownloadDocument = (salesDocument: Invoice | Receipt) => {
    if (currentOrder && token) {
      downloadSaleOrderDocument(currentOrder, salesDocument, token)
    }
  }

  return (
    <div className={styles.wrapper}>
      <div
        className={styles.backButton}
        id='profile_order_back_button'
        onClick={handleGoBack}
      >
        <IconArrowLeft />
        <p>Wróć do listy zamówień</p>
      </div>
      {isEditable && (
        <EditSaleOrderLabel
          className={styles.editOrderLabel}
          currentOrder={currentOrder}
        />
      )}
      <div className={styles.header}>
        <div>
          <div className={styles.orderNumber}>Zamówienie: #{code}</div>
          <div className={styles.statusWrapper}>
            {status && <SaleOrderStatus status={status} />}
            {returnStatus && <OrderReturnStatusBadge status={returnStatus} />}
          </div>
        </div>
        <div className={styles.buttonsWrapper}>
          {returnStatus === SaleOrderReturnStatus.AVAILABLE && (
            <OrderReturnModal
              order={currentOrder}
              triggerButtonSize={ButtonSizes.small}
              onSubmitted={handleReturnSubmitted}
            />
          )}
          {documentsList.length > 0 ? (
            documentsList.map((document, index) => (
              <Tooltip
                key={document.id}
                id={`profile-order-document-button-${document.id}-tooltip`}
                title={`Pobierz Dokument ${index + 1}`}
              >
                <div>
                  <Button
                    id={`profile_order_document_button_${index}`}
                    variant='neutralOutline'
                    startIcon={<IconFileDownload />}
                    onClick={() => handleDownloadDocument(document)}
                    size={ButtonSizes.small}
                  >
                    {`Pobierz Dokument ${index + 1}`}
                  </Button>
                </div>
              </Tooltip>
            ))
          ) : (
            <Tooltip
              id='profile-order-document-button-disabled-tooltip'
              title='Dokumenty będą dostępne po dostarczeniu zamówienia'
            >
              <div>
                <Button
                  id='profile_order_document_button_disabled'
                  variant='neutralOutline'
                  size={ButtonSizes.small}
                  startIcon={<IconFileDownload />}
                  onClick={noop}
                  disabled
                >
                  Brak dokumentów
                </Button>
              </div>
            </Tooltip>
          )}

          <Button
            variant='neutralOutline'
            startIcon={<IconPrinter />}
            onClick={handleGenerateOrderPdf}
            fullWidth={false}
            size={ButtonSizes.small}
          >
            Wydrukuj
          </Button>

          <RepeatSaleOrderButton
            id='profile_order_repeat_button'
            items={items}
            triggerButtonSize={ButtonSizes.small}
          />
          {isCancelEnabledByStatus && (
            <CancelSaleOrderModal
              testId='profile_order_cancel_button'
              order={currentOrder}
              triggerButtonSize={ButtonSizes.small}
            />
          )}
        </div>
      </div>

      <div className={styles.divider} />
      <ProfileSaleOrderDetailsTable
        order={currentOrder}
        isTmsDelivery={IS_TMS_DELIVERY}
        handlePaymentStart={handlePaymentStart}
      />

      <div className={styles.divider} />
      <ProfileSaleOrderDetailsProductList saleOrderItems={items} />
    </div>
  )
}

export default ProfileSaleOrderDetails
